import { useCallback } from 'react';
import i18n from 'i18next';
import axios, { useApiRequest } from './axios';
import { ConsentKind, ConsentState, DocumentAcceptance, LegalDocument } from '../types/legal';

const DOCUMENTS_URL = '/legal/documents/';
const DOCUMENTS_ARCHIVE_URL = '/legal/documents/archive/';
const ACCEPTANCES_URL = '/legal/acceptances/';
const PENDING_ACCEPTANCES_URL = '/legal/acceptances/pending/';
const CONSENTS_URL = '/legal/consents/';

// The published documents barely change within a session, yet several
// components load them independently on the same screen - the registration
// form and the public footer both do. Share one request per language so a
// screen never asks for the same list twice; keyed by language because the
// backend returns the documents translated.
const documentsByLanguage = new Map<string, Promise<LegalDocument[]>>();

export const useLegalDocumentsRequests = () => {
  const { loading, error, executeRequest } = useApiRequest();

  // Public endpoint - reachable before login, used by the registration form
  // and the footer of the public pages.
  const query = useCallback(async (): Promise<LegalDocument[]> => {
    const language = i18n.language;
    let request = documentsByLanguage.get(language);
    if (!request) {
      request = executeRequest(() => axios.get(DOCUMENTS_URL)).then(({ data, success }) => {
        // Don't cache a failure - the next caller should be able to retry.
        if (!success) {
          documentsByLanguage.delete(language);
        }
        return data || [];
      });
      documentsByLanguage.set(language, request);
    }
    return request;
  }, [executeRequest]);

  const queryArchive = useCallback(async (): Promise<LegalDocument[]> => {
    const { data } = await executeRequest(() => axios.get(DOCUMENTS_ARCHIVE_URL));
    return data || [];
  }, [executeRequest]);

  return { loading, error, query, queryArchive };
};

export const useDocumentAcceptancesRequests = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const queryPending = useCallback(async (): Promise<LegalDocument[]> => {
    const { data } = await executeRequest(() => axios.get(PENDING_ACCEPTANCES_URL));
    return data || [];
  }, [executeRequest]);

  const queryMine = useCallback(async (): Promise<DocumentAcceptance[]> => {
    const { data } = await executeRequest(() => axios.get(ACCEPTANCES_URL));
    return data || [];
  }, [executeRequest]);

  const accept = useCallback(
    async (documentIds: number[]) => {
      return await executeRequest(() =>
        axios.post(ACCEPTANCES_URL, { document_ids: documentIds })
      );
    },
    [executeRequest]
  );

  return { loading, error, queryPending, queryMine, accept };
};

export const useConsentsRequests = () => {
  const { loading, error, executeRequest } = useApiRequest();

  const query = useCallback(async (): Promise<ConsentState | null> => {
    const { data } = await executeRequest(() => axios.get(CONSENTS_URL));
    return data || null;
  }, [executeRequest]);

  const update = useCallback(
    async (kind: ConsentKind, granted: boolean) => {
      return await executeRequest(() => axios.post(CONSENTS_URL, { kind, granted }));
    },
    [executeRequest]
  );

  return { loading, error, query, update };
};
